import { useState, useMemo } from "react";
import {
  List,
  useTable,
  TableColumnType,
  Button,
  Table,
  SaveButton,
  Form,
  Input,
  FilterDropdown,
  FilterDropdownProps,
  Radio,
  Popconfirm,
} from "@pankod/refine-antd";
import { ISearch } from "type";
import { useExport, useCustomMutation, useNotification } from "@pankod/refine-core";
import {
  renderNumber,
  renderString,
  getPercentColor,
  toPercent,
} from "utils/render";
import { REST_PUBLIC_URI } from "environment";

export const SubscriptionStatList = () => {
  const [query, setQuery] = useState("");
  const { tableProps, searchFormProps, filters, tableQueryResult: { refetch } } = useTable({
    queryOptions: { retry: false },
    initialFilter: [
      {
        field: "email",
        operator: "eq",
        value: "",
      },
      {
        field: "name",
        operator: "eq",
        value: "",
      },
      {
        field: "tier",
        operator: "eq",
        value: "",
      },
      {
        field: "status",
        operator: "eq",
        value: "",
      }
    ],
  });
  const { triggerExport } = useExport({
    filters: filters,

    mapData: (item) => {
      return {
        ...item,
        status: renderStatusText(item.status),
        profit_percent: isNaN(parseFloat(item.profit_percent))
          ? ""
          : parseFloat(item.profit_percent) * 100 + "%",
      };
    },
  });

  const { mutate } = useCustomMutation();
  const { open } = useNotification();

  const renderStatusText = (status: number) => {
    switch (status) {
      case 0: return "NONE";
      case 1: return "GONE";
      case 2: return "RUNNING";
      case 3: return "FROZEN";
      default: return "";
    }
  };

  const renderStatus = (status: number) => {
    switch (status) {
      case 0: return <span>NONE</span>;
      case 1: return <span style={{ color: "#6b7280" }}>GONE</span>;
      case 2: return <span style={{ color: "#16a34a" }}>RUNNING</span>;
      case 3: return <span style={{ color: "#d97706" }}>FROZEN</span>;
      default: return <span></span>;
    }
  };

  const handleFreeze = (subId: number) => {
    mutate(
      { url: `${REST_PUBLIC_URI}/api/admin/v1/bot/subscriptions/freeze/${subId}`, method: "post", values: {} },
      {
        onSuccess: () => {
          refetch();
          open?.({ type: "success", message: `Subscription ${subId} frozen` });
        },
        onError: (err) => {
          open?.({ type: "error", message: `Freeze failed: ${err.message}` });
        },
      }
    );
  };

  const handleUnfreeze = (subId: number) => {
    mutate(
      { url: `${REST_PUBLIC_URI}/api/admin/v1/bot/subscriptions/unfreeze/${subId}`, method: "post", values: {} },
      {
        onSuccess: () => {
          refetch();
          open?.({ type: "success", message: `Subscription ${subId} unfrozen` });
        },
        onError: (err) => {
          open?.({ type: "error", message: `Unfreeze failed: ${err.message}` });
        },
      }
    );
  };

  const columns = useMemo<TableColumnType<any>[]>(
    () => [
      {
        dataIndex: "bot_subscription_id",
        title: "Bot-sub Id",
        width: 150,
      },
      {
        dataIndex: "user_id",
        title: "User Id",
        width: 150,
      },
      {
        dataIndex: "email",
        title: "Email",
        width: 250,
        filterDropdown: (props: FilterDropdownProps) => {
          return (
            <FilterDropdown {...props}>
              <Input
                placeholder="Search email"
                onPressEnter={(e) => {
                  props.confirm();
                }}
              />
            </FilterDropdown>
          );
        }
      },
      {
        dataIndex: "name",
        title: "Name",
        width: 250,
        filterDropdown: (props: FilterDropdownProps) => {
          return (
            <FilterDropdown {...props}>
              <Input
                placeholder="Search bot name"
                onPressEnter={(e) => {
                  props.confirm();
                }}
              />
            </FilterDropdown>
          );
        },
      },
      {
        dataIndex: "initial_amount",
        title: "Initial Amount",
        width: 200,
      },
      {
        dataIndex: "status",
        title: "Status",
        width: 200,
        render: (_, record) => {
          return renderStatus(record.status);
        },
        filterDropdown: (props: FilterDropdownProps) => {
          return (
            <FilterDropdown {...props}>
              <Radio.Group>
                <Radio value="0">NONE</Radio>
                <Radio value="1">GONE</Radio>
                <Radio value="2">RUNNING</Radio>
                <Radio value="3">FROZEN</Radio>
              </Radio.Group>
            </FilterDropdown>
          );
        },
      },
      {
        dataIndex: "nav",
        title: "Nav",
        width: 150,
        render: (_, record) => {
          const style = {
            textAlign: "right",
          };
          return renderNumber(record.nav, undefined, undefined, { style });
        },
      },
      {
        dataIndex: "profit",
        title: "Profit",
        width: 150,
        render: (_, record) => {
          const style = {
            textAlign: "right",
            color: getPercentColor(parseFloat(record.profit)),
          };
          return renderNumber(record.profit, undefined, undefined, { style });
        },
      },
      {
        dataIndex: "profit_percent",
        title: "Profit Percent",
        width: 200,
        render: (_, record) => {
          const style = {
            textAlign: "right",
            color: getPercentColor(parseFloat(record.profit_percent)),
          };
          return renderNumber(record.profit_percent, 4, toPercent, { style });
        },
      },
      {
        dataIndex: "tier",
        title: "Tier",
        width: 200,
        filterDropdown: (props: FilterDropdownProps) => {
          return (
            <FilterDropdown {...props}>
              <Input
                placeholder="Search tier name"
                onPressEnter={(e) => {
                  props.confirm();
                }}
              />
            </FilterDropdown>
          );
        }
      },
      {
        dataIndex: "tier_time_left",
        title: "Tier Time Left",
        width: 200,
        render: (_, record) => {
          let days = record.tier_time_left.split(" ")[0];
          const style = {
            color: days < 30 ? "red" : "black",
          };
          return renderString(record.tier_time_left, undefined, { style });
        },
      },
      {
        title: "Actions",
        width: 150,
        render: (_, record) => {
          if (record.status === 2) {
            return (
              <Popconfirm
                title="Freeze this subscription?"
                onConfirm={() => handleFreeze(record.bot_subscription_id)}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" danger>Freeze</Button>
              </Popconfirm>
            );
          }
          if (record.status === 3) {
            return (
              <Button size="small" onClick={() => handleUnfreeze(record.bot_subscription_id)}>
                Unfreeze
              </Button>
            );
          }
          return null;
        },
      },
    ],
    [refetch]
  );
  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button type="primary" onClick={triggerExport}>
            EXPORT
          </Button>
        </>
      )}
    >
      <Table
        {...tableProps}
        bordered={true}
        rowKey="bot_subscription_id"
        columns={columns}
        scroll={{ x: 1500 }}
        size="middle"
        pagination={{
          ...tableProps.pagination,
          position: ["bottomRight"],
          size: "default",
        }}
      ></Table>
    </List>
  );
};
